<template>
  <div>
    <b-row :key="model.params.packageType + model.distribution">
      <b-col sm="12">
         <b-overlay :show="searchIsRunning" rounded>
          <b-card>
            <h3 class="chr-h3"><b-icon :icon="searchIcon"></b-icon> {{ searchName }}</h3>
            <SearchForm
              :params="model.params"
              :key="'searchFormFor' + model.params.packageType"
              @search="onSearch"
              @openFees="onOpenFees"
            />
          </b-card>
         </b-overlay>
        <template v-if="!searchIsRunning && model.params.done">
          <b-card class="my-3">
            <b-row>
              <b-col sm="12" class="mb-2">
              <SearchResultsSummary
                :done="model.params.done"
                :hasTransport="model.hasTransport"
                :resortName="model.params.resortName"
                :duration="model.getDuration()"
                :noResults="model.results.length"
                :date="date"
              />
              </b-col>
            </b-row>
            <SortFields
                @sort="onSort"
                @pagination="onPaginate"
                :hotelThemes="model.hotelThemes"
                :hotelTypes="model.hotelTypes"
                :hotelAmenities="model.hotelAmenities"
                :roomAmenities="model.roomAmenities"
                :priceFilters="this.priceFilters"
                :selectedPriceFilters="this.selectedPriceFilters"
                :selectedHotelThemes="selectedHotelThemes"
                :selectedHotelTypes="selectedHotelTypes"
                :selectedHotelAmenities="selectedHotelAmenities"
                :selectedRoomAmenities="selectedRoomAmenities"
                :confirmedOnly="confirmedOnly"
                :freeChildOnly="freeChildOnly"
                @priceFiltersChange="onPriceFiltersChange"
                @hotelThemesChange="onHotelThemesChange"
                @hotelTypesChange="onHotelTypesChange"
                @hotelAmenitiesChange="onHotelAmenitiesChange"
                @roomAmenitiesChange="onRoomAmenitiesChange"
                @confirmedOnlyChange="confirmedOnly = $event"
                @freeChildOnlyChange="freeChildOnly = $event"
                @filtersReset="onFiltersReset"
                v-if="model.results.length"
                />
          </b-card>
        </template>
        <template v-if="model.results.length">
          <b-card>
            <Legend/>
            <!--  can this table become a component?-->
            <b-table-simple class="table" responsive small style="font-size: 0.9rem;">
              <ResultsHeader
                :transport="model.params.transport"
                :categoriesCollapsed="categoriesCollapsed"
                :mealsCollapsed="mealsCollapsed"
                @toggleCategories="categoriesCollapsed = !categoriesCollapsed"
                @toggleMeals="mealsCollapsed = !mealsCollapsed"
                @toggleChecked = "onToggleChecked"
              />
              <b-tbody>
                <template
                  v-for="(result,resultIndex) in paginatedResults"
                >
                  <PackageResult
                    v-for="(pack, packIndex) in result.packages"
                    :key="'pack' + resultIndex + packIndex"
                    :result="result"
                    :pack="pack"
                    :allMealsCollapsed="mealsCollapsed"
                    :allCategoriesCollapsed="categoriesCollapsed"
                    :transport="model.params.transport"
                    :selected="isSelected(result)"
                    @book="onBook({ resultId: result.id, packageId: pack.id, ...$event })"
                    @toggle="onToggle(result)"
                    @info="onInfo([result])"
                    @priceDetails="onPriceDetails({ result, pack, ...$event })"
                  />
                  </template>
              </b-tbody>
            </b-table-simple>
            <Legend/>
          </b-card>
          <b-card no-body class="p-2 mt-3">
            <b-pagination
              v-model="currentPage"
              :totalRows="model.results.length"
              :per-page="resultsPerPage"
              @change="scrollToTop()"
              align="center"
              class="mt-4"
              />
          </b-card>
        </template>
      </b-col>
    </b-row>
    <SelectedResults
      :results="selectedResults"
      v-show="selectedResults.length"
      @hide="onHideSelected()"
      @info="onInfoSelected()"
    />

    <!-- fees modal -->
    <b-modal size="lg" hide-header hide-footer v-model="feesModal">
      <SimpleModalHeader @closeModal="onCloseFeesModal"/>
      <ServiceTax/>
      <ModalFooter @closeModal="onCloseFeesModal"/>
    </b-modal>

    <!-- detail modal -->
    <b-modal v-model="modal" :title="$gettext('Modal Info')" size="xl" hide-header hide-footer>
      <div id="printArea">
        <ModalHeader @print="onPrint()" @download="onDownload()" @closeModal="onModalClose" />
        <b-card class="bg-light" no-body>
          <SearchParamsSummary :params="model.params"/>
        </b-card>
        <b-row v-for="result in infoResults" :key="'detailedInfo' + result.id" class="p-4">
          <DetailedInfo
            :result="result"
            :currency="model.params.currency"
          ></DetailedInfo>
        </b-row>
      </div>
      <ModalFooter @closeModal="onModalClose" />
    </b-modal>
    <!-- price details modal -->
    <b-modal v-model="priceDetailsModal" :title="$gettext('Price Details')" size="xl" hide-header hide-footer>
      <SimpleModalHeader @closeModal="closePriceDetailsModal" />
      <PriceDetails
        :result="priceDetailsParams.result"
        :title="priceDetailsParams.title"
        :meal="priceDetailsParams.meal"
        :serviceTax="priceDetailsParams.serviceTax"
        :discounts="priceDetailsParams.discounts"
        :paymentSchedule="priceDetailsParams.paymentSchedule"
      />
      <ModalFooter @closeModal="closePriceDetailsModal" />
    </b-modal>

  </div>
</template>

<script>
import moment from 'moment'
import model from './services/model'
import { routeGuard } from './router'
import viewMixin from '../shared/mixins/search_views'
import SearchForm from './components/search_form'
import ServiceTax from '../shared/components/service_tax'
import SimpleModalHeader from '../shared/components/simple_modal_header'
import ModalFooter from '../shared/components/modal_footer'
import SearchResultsSummary from '../shared/components/search_results_summary'
import SortFields from '../shared/components/sort_fields'
import Legend from '../shared/components/legend'
import ResultsHeader from '../shared/components/results_table_head'
import PackageResult from './components/package_result'
import SelectedResults from '@/app/private/shared/components/selected_results'
import ModalHeader from '@/app/private/shared/components/modal_header'
import DetailedInfo from './components/detailed_info'
import SearchParamsSummary from './components/search_params_summary'
import PriceDetails from '@/app/private/shared/components/price_details'

import { paymentSchedule } from 'b2b-common/services/api/package'

export default {
  beforeRouteUpdate (to, from, next) {
    routeGuard(to, from, next)
  },
  components: {
    SearchForm,
    ServiceTax,
    SimpleModalHeader,
    ModalFooter,
    SearchResultsSummary,
    SortFields,
    Legend,
    SelectedResults,
    PackageResult,
    ResultsHeader,
    ModalHeader,
    DetailedInfo,
    SearchParamsSummary,
    PriceDetails
  },
  mixins: [viewMixin],
  data () {
    const priceModifier = model.params.currency + '/pp/pn'
    const priceFilters = [
      { text: ' < 100 ' + priceModifier, value: { min: Number.MIN_SAFE_INTEGER, max: 100 } },
      { text: '100 - 500 ' + priceModifier, value: { min: 100, max: 500 } },
      { text: '500 - 1000 ' + priceModifier, value: { min: 500, max: 1000 } },
      { text: '> 1000 ' + priceModifier, value: { min: 1000, max: Number.MAX_SAFE_INTEGER } }
    ]
    return {
      model,
      feesModal: false,
      categoriesCollapsed: false,
      mealsCollapsed: true,
      priceDetailsParams: {
        result: {},
        meal: null,
        title: null,
        discounts: [],
        paymentSchedule: []
      },
      priceFilters,
      selectedPriceFilters: [],
      selectedHotelThemes: [],
      selectedHotelTypes: [],
      selectedHotelAmenities: [],
      selectedRoomAmenities: [],
      confirmedOnly: false,
      freeChildOnly: false
    }
  },
  computed: {
    searchName () {
      if (this.model.params.packageType === 'package') {
        if (this.model.params.distribution === 3) return this.$gettext('SENIOR VOYAGE')
        return this.$gettext('PACKAGES')
      }
      if (this.model.params.distribution === 2) return this.$gettext('SENIOR VOYAGE & ISRAEL')
      return this.$gettext('TOURS')
    },
    searchIcon () {
      if (this.model.params.packageType === 'package' && this.model.params.distribution === 2) return 'wallet-fill'
      return 'globe2'
    },
    paginatedResults () {
      return this.filteredResults
        .slice(
          (this.currentPage - 1) * this.resultsPerPage,
          this.currentPage * this.resultsPerPage
        )
    },
    filteredResults () {
      const noNights = this.model.getDuration()
      const noPax = this.model.getNoPax()
      let filtered = [...this.model.results]

      // no filters and all filters should work the same (at least that's what I think now)
      if (this.selectedPriceFilters.length && this.priceFilters.length > this.selectedPriceFilters.length) {
        // this might become too slow, so we might need to move this to the model
        // of actutally using array.some to check if any elements in the various arrays match the condition
        // but keep all results with matching elements

        // apply price filters
        filtered = filtered
          // map results to filter out any categories that don't match the price filters
          .map(el => ({
            ...el,
            packages: el.packages
              .map(el1 => ({
                ...el1,
                categories: el1.categories
                  .map(el2 => ({
                    ...el2,
                    meals: el2.meals.filter(el3 => {
                      const price = (el3.price + el3.tax) / noNights / noPax
                      return this.selectedPriceFilters.some(el4 => (price > el4.min) && (price <= el4.max))
                    })
                  }))
                  // and then filter out any categories that don't have any meals left
                  .filter(el2 => el2.meals.length)
              }))
              // and then filter out any packages that don't have any categories left
              .filter(el1 => el1.categories.length)
          }))
          // and now filter out any results that don't have any categories left
          .filter(el => el.packages.length)
      }

      if (this.confirmedOnly) {
        filtered = filtered.filter(el =>
          el.packages.some(el1 => el1.categories.some(el2 => el2.hotelIsAvailable))
        )
      }

      if (this.freeChildOnly) {
        filtered = filtered.filter(el =>
          el.packages.some(el1 => el1.categories.some(el2 => el2.isFreePax))
        )
      }

      if (this.selectedHotelTypes.length && (this.selectedHotelTypes.length < this.model.hotelTypes.length)) {
        filtered = filtered.filter(el => this.selectedHotelTypes.includes(el.property_type?.id))
      }

      if (this.selectedHotelThemes.length && (this.selectedHotelThemes.length < this.model.hotelThemes.length)) {
        filtered = filtered.filter(
          el => this.selectedHotelThemes.some(
            el2 => el.hotel_themes?.some(el3 => el3.id === el2) ?? false
          )
        )
      }

      if (this.selectedHotelAmenities.length) {
        // need to filter out any results that don't have all hotel amenities
        // selectedHotelAmenities is an array of ids
        // hotel_amenities is an array of objects with id and name
        // so we need to filter out any results that don't have all the ids in selectedHotelAmenities

        filtered = filtered.filter(el => {
          const hotelAmenities = el.hotel_amenities.map(el2 => el2.id)
          return this.selectedHotelAmenities.every(el2 => hotelAmenities.includes(el2))
        })
      }

      if (this.selectedRoomAmenities.length) {
        // same principle as hotel amenities
        filtered = filtered.filter(el => {
          const roomAmenities = el.room_amenities.map(el2 => el2.id)
          return this.selectedRoomAmenities.every(el2 => roomAmenities.includes(el2))
        })
      }

      return filtered
    },
    date () {
      return moment.unix(this.model.params.date).format('DD/MM/YYYY')
    }
  },
  methods: {
    onOpenFees () {
      this.feesModal = true
    },
    onCloseFeesModal () {
      this.feesModal = false
    },
    onBook ({ resultId, packageId, categoryId, mealIndex }) {
      let pType = this.model.params.packageType
      pType = pType[0].toUpperCase() + pType.slice(1)
      const route = {
        name: 'Private/' + pType + '/Book',
        params: {
          DistributionChannel: this.model.distribution,
          Result: resultId,
          Package: packageId,
          Category: categoryId,
          Meal: mealIndex
        }
      }
      this.$router.push(route)
    },
    async onPriceDetails ({ result, pack, category, meal }) {
      this.priceDetailsParams.result = result
      this.priceDetailsParams.title = pack.name
      this.priceDetailsParams.meal = meal
      this.priceDetailsParams.discounts = []
      if (Array.isArray(category.discounts)) this.priceDetailsParams.discounts = this.priceDetailsParams.discounts.concat(category.discounts)
      if (Array.isArray(pack.discounts)) this.priceDetailsParams.discounts = this.priceDetailsParams.discounts.concat(pack.discounts)
      this.priceDetailsParams.paymentSchedule = await paymentSchedule({
        packageType: this.model.params.packageType,
        category: category.id,
        meal: meal.id
      })
      this.priceDetailsModal = true
    },
    toggleAllCategories () {
      this.categoriesToggled = !this.categoriesToggled
    },
    toggleAllMeals () {
      this.mealsToggled = !this.mealsToggled
    },
    onPriceFiltersChange (val) {
      // remove all elements form selectedPriceFilters that are not in val
      this.selectedPriceFilters.splice(0, this.selectedPriceFilters.length, ...val)
    },
    onHotelAmenitiesChange (val) {
      this.selectedHotelAmenities.splice(0, this.selectedHotelAmenities.length, ...val)
    },
    onRoomAmenitiesChange (val) {
      this.selectedRoomAmenities.splice(0, this.selectedRoomAmenities.length, ...val)
    },
    onHotelThemesChange (val) {
      this.selectedHotelThemes.splice(0, this.selectedHotelThemes.length, ...val)
    },
    onHotelTypesChange (val) {
      this.selectedHotelTypes.splice(0, this.selectedHotelTypes.length, ...val)
    },
    onToggleChecked (checked) {
      this.selectedResults = checked ? this.model.results : []
    },
    onFiltersReset () {
      this.selectedHotelThemes.splice(0, this.selectedHotelThemes.length)
      this.selectedHotelTypes.splice(0, this.selectedHotelTypes.length)
      this.selectedHotelAmenities.splice(0, this.selectedHotelAmenities.length)
      this.selectedRoomAmenities.splice(0, this.selectedRoomAmenities.length)
      this.selectedPriceFilters.splice(0, this.selectedPriceFilters.length)
      this.confirmedOnly = false
      this.freeChildOnly = false
    }
  }
}
</script>

<style lang="scss">
  .chr-h3{
    border-bottom: 1px solid #424242;
    padding-bottom: .7rem;
  }
</style>
